import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import { type ReplayTimingController } from "./use-replay-timing";
import { replayTerminals, type ReplayIdentity } from "./replay-timing";
import {
  type ReplayTick,
  type ViewerMessage,
  parseViewerMessage,
  recordReplayPhase,
  validTick,
} from "./replay-messages";
import { captureReplayFailure } from "./replay-failure-report";

const replayViewerReadyDeadlineMs = 120000;

export function useReplayReadiness({
  coworldId,
  telemetryContext,
  surface,
  telemetry,
  onTick,
}: ReplayIdentity & {
  telemetry: ReplayTimingController;
  onTick?: ((tick: ReplayTick) => void) | undefined;
}) {
  const { readTiming, recordTiming, hasTerminalOutcome, captureTerminal } =
    telemetry;
  const [painted, setPainted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframe = useRef<HTMLIFrameElement | null>(null);
  const viewer = useRef({ announced: false, ready: false, failed: false });
  const deadline = useRef<number | undefined>(undefined);
  const paintFrame = useRef(0);
  const followingFrame = useRef(0);
  const reportTick = useEffectEvent((tick: ReplayTick) => onTick?.(tick));
  const clearDeadline = useCallback(() => {
    window.clearTimeout(deadline.current);
    deadline.current = undefined;
  }, []);
  const setIframeNode = useCallback(
    (node: HTMLIFrameElement | null) => {
      iframe.current = node;

      if (node !== null && readTiming().iframeInsertedAt === null)
        recordTiming({ iframeInsertedAt: performance.now() });
    },
    [readTiming, recordTiming],
  );
  const onLoad = useCallback(() => {
    recordTiming({ iframeLoadedAt: performance.now() });
    paintFrame.current = window.requestAnimationFrame(() => {
      followingFrame.current = window.requestAnimationFrame(() => {
        if (viewer.current.announced) return;

        captureTerminal(replayTerminals.iframeReady);
        setPainted(true);
      });
    });
  }, [recordTiming, captureTerminal]);
  const onError = useCallback(() => {
    captureTerminal(replayTerminals.iframeError);
    setError("Replay viewer document failed to load.");
  }, [captureTerminal]);
  const failViewer = useEffectEvent((data: ViewerMessage) => {
    if (viewer.current.failed) return;

    viewer.current.failed = true;
    const message = captureReplayFailure({
      data,
      viewerReady: viewer.current.ready,
      timing: readTiming(),
      coworldId,
      telemetryContext,
      surface,
    });
    clearDeadline();
    captureTerminal(replayTerminals.viewerError);
    setError(message);
  });
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!iframe.current || event.source !== iframe.current.contentWindow)
        return;

      const data = parseViewerMessage(event.data);

      if (data === null) return;

      switch (data.type) {
        case "loading":
          viewer.current.announced = true;

          if (deadline.current === undefined) {
            deadline.current = window.setTimeout(() => {
              deadline.current = undefined;

              if (hasTerminalOutcome()) return;

              captureTerminal(replayTerminals.viewerTimeout);
              // Keep the iframe alive so a slow viewer can still finish after the overlay deadline.
              setPainted(true);
            }, replayViewerReadyDeadlineMs);
          }

          break;
        case "phase":
          recordReplayPhase({ data, recordTiming });
          break;
        case "tick":
          if (validTick(data))
            reportTick({ tick: data.tick, playing: data.playing });

          break;
        case "ready":
          if (viewer.current.failed) return;

          viewer.current.ready = true;
          clearDeadline();
          captureTerminal(replayTerminals.viewerReady);
          setPainted(true);
          break;
        case "error":
          failViewer(data);
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      clearDeadline();

      window.cancelAnimationFrame(paintFrame.current);
      window.cancelAnimationFrame(followingFrame.current);
    };
  }, [captureTerminal, clearDeadline, recordTiming, hasTerminalOutcome]);
  return { painted, error, setError, setIframeNode, onLoad, onError };
}
